import React, { useRef, useEffect, useState } from 'react';
import axios from 'axios';
import Tooltip from '../../components/utils/Tooltip';


const GridCanvas = ({ width = 400, height = 400, rows = 5, cols = 5, onClose }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [convertedText, setConvertedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const drawColor = 'black'; // Set the drawing color
  const [selectedButton, setSelectedButton] = useState(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas before drawing the grid
    ctx.clearRect(0, 0, width, height);

    // Set canvas background color
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, height);
    
    // Draw the grid
    const cellWidth = width / cols;
    const cellHeight = height / rows;

    ctx.strokeStyle =  '#ADD8E6';  // Light gray grid lines
    ctx.lineWidth = 1;

    for (let i = 0; i <= cols; i++) {
      const x = i * cellWidth;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }

    for (let i = 0; i <= rows; i++) {
      const y = i * cellHeight;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  }, [width, height, rows, cols]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const startDrawing = (offsetX, offsetY) => {
      ctx.strokeStyle = drawColor;  // Set stroke color
      ctx.lineWidth = 1;  // Set line width for drawing
      ctx.beginPath();
      ctx.moveTo(offsetX, offsetY);
      setIsDrawing(true);
    };

    const draw = (offsetX, offsetY) => {
      if (!isDrawing) return;
      ctx.lineTo(offsetX, offsetY);
      ctx.stroke();
    };

    const endDrawing = () => {
      if (isDrawing) {
        setIsDrawing(false);
        ctx.beginPath(); // Reset the path
      }
    };

    const handleMouseDown = (e) => {
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      startDrawing(offsetX, offsetY);
    };

    const handleMouseMove = (e) => {
      if (!isDrawing) return;
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const offsetY = e.clientY - rect.top;
      draw(offsetX, offsetY);
    };

    const handleTouchStart = (e) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.touches[0].clientX - rect.left;
      const offsetY = e.touches[0].clientY - rect.top;
      startDrawing(offsetX, offsetY);
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      if (!isDrawing) return;
      const rect = canvas.getBoundingClientRect();
      const offsetX = e.touches[0].clientX - rect.left;
      const offsetY = e.touches[0].clientY - rect.top;
      draw(offsetX, offsetY);
    };

    const handleMouseUp = endDrawing;
    const handleMouseOut = endDrawing;
    const handleTouchEnd = endDrawing;

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseout', handleMouseOut);
    canvas.addEventListener('touchstart', handleTouchStart);
    canvas.addEventListener('touchmove', handleTouchMove);
    canvas.addEventListener('touchend', handleTouchEnd);

    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseout', handleMouseOut);
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDrawing, drawColor]);

  const handleUploadImage = async () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const dataURL = canvas.toDataURL('image/png');
      const response = await fetch(dataURL);
      const blob = await response.blob();
      const formData = new FormData();
      formData.append('image', blob, 'canvas-image.png');
      await axios.post('http://localhost:5000/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alert('Image uploaded successfully!');
    }
  };

  const handleButtonClick = (buttonName, callback) => {
    setSelectedButton(buttonName);
    if (callback) callback();
  };

  
  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mimeString });
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);
  };

  // Download canvas as image
    const handleDownloadImage = () => {
      const canvas = canvasRef.current;
      const dataURL = canvas.toDataURL();
      const link = document.createElement('a');
      link.href = dataURL;
      link.download = `canvas_image_${Date.now()}.png`;
      link.click();
    };
  
    // Download canvas as PDF
    const handleDownloadPDF = () => {
      const canvas = canvasRef.current;
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (canvas.height / canvas.width) * imgWidth;
      pdf.addImage(canvas.toDataURL(), 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`canvas_pdf_${Date.now()}.pdf`);
    };

  return (
    <div style={{ marginTop: '10px', position: 'relative' }}>
      <canvas
        ref={canvasRef}
        width={width}  // Adjust this width as needed
        height={height} // Adjust this height as needed
        style={{ 
          border: '1px solid #ccc', 
          backgroundColor: 'white', 
          display: 'block', // Ensure the canvas is not stretched vertically
          margin: '0 auto' // Center the canvas horizontally if needed
        }}
      />
      <div className="flex flex-row justify-center gap-2 px-2 mt-2">
        <Tooltip text="Clear Canvas">
          <span
            className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
              selectedButton === 'clear' ? 'bg-pink-400' : 'bg-pink-600 text-white'
            }`}
            onClick={() => handleButtonClick('clear', clearCanvas)}
          >
             <i className="fa-solid fa-undo"></i>
          </span>
        </Tooltip>

        <Tooltip text="Upload Image">
          <span
            className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
              selectedButton === 'upload' ? 'bg-pink-400' : 'bg-pink-600 text-white'
            }`}
            onClick={() => handleButtonClick('upload', handleUploadImage)}
          >
            <i className="fa-solid fa-upload"></i>
          </span>
        </Tooltip>

        <Tooltip text="Download Image">
          <span
            className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
              selectedButton === 'downloadImage' ? 'bg-pink-400' : 'bg-pink-600 text-white'
            }`}
            onClick={() => handleButtonClick('downloadImage', handleDownloadImage)}
          >
             <i className="fa-solid fa-download"></i>
          </span>
        </Tooltip>

        <Tooltip text="Download PDF">
          <span
            className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
              selectedButton === 'downloadPDF' ? 'bg-pink-400' : 'bg-pink-600 text-white'
            }`}
            onClick={() => handleButtonClick('downloadPDF', handleDownloadPDF)}
          >
            <i className="fa-solid fa-file-pdf"></i>
          </span>
        </Tooltip>

        <Tooltip text="Close">
          <span
            className={`w-12 h-8 flex items-center justify-center cursor-pointer rounded-lg ${
              selectedButton === 'close' ? 'bg-pink-400' : 'bg-pink-600 text-white'
            }`}
            onClick={() => handleButtonClick('close', onClose)}
          >
             <i className="fa-solid fa-arrow-right-from-bracket"></i>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default GridCanvas;
